import { Component, OnInit, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { ToolRequestsService } from '@services/tool-requests.service';
import { Line } from '@models/line.model';
import { ToolRequest } from '@models/toolRequests.model';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Tool } from '@app/tool/model/tool.model';
import { ToolReturnsService } from '@services/tool-returns.service';
import { ToolSet } from '@app/tool/model/toolSet.model';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import { PermissionService } from '@app/authentication/services/permission.service';
import { ToolAtErsaConfirmationModalComponent } from './components/tool-ersa-confirmation-modal/tool-ersa-confirmation-modal.component';
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../environments/environment';

enum ToolReturnSteps {
    SELECT_LINE_AND_TOOL_REQUEST = 0,
    CONFIRM = 1
}

@Component({
    selector: 'app-tool-return',
    templateUrl: './tool-return.component.html',
    styleUrls: ['./tool-return.component.scss']
})
export class ToolReturnComponent implements OnInit {
    _logger = Log.create(ToolReturnComponent.name);

    @ViewChild('successModal', {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('successReasonModal', {static: true}) reasonConfirmationModal: ConfirmationModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;
    @ViewChild(ToolAtErsaConfirmationModalComponent, {static: true}) ersaModal: ToolAtErsaConfirmationModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;
    currentStep = 1;
    firstNextTried = false;

    selectedLine: Line;
    isLoading;
    toolsLoading = false;

    selectedToolRequest: ToolRequest;

    toolsNeededAtLine: Array<Tool | ToolSet> = [];
    toolsNeededToBeReturned: Array<Tool | ToolSet> = [];
    toolsExcludedFromReturn: Array<Tool | ToolSet> = [];
    toolsReservedAtLine: Array<Tool | ToolSet> = [];
    toolsThatStayInERSA: Array<Tool | ToolSet> = [];

    confirmationPopupMessage: string;
    isConfirmationInProgress: boolean;
    returnReason: string;
    toolsThatStayInERSareIncludedInReturn = true;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(
        private translateService: TranslateService,
        private permissionService: PermissionService,
        private toolReturnService: ToolReturnsService,
        private toolRequestService: ToolRequestsService
    ) {
    }

    ngOnInit() {
    }

    onLineChanged(line: Line) {
        this.selectedToolRequest = null;
        if (line && line instanceof Line) {
            this.selectedLine = line;
        }
    }

    onToolRequestChanged(toolRequest: ToolRequest) {
        if (toolRequest && toolRequest instanceof ToolRequest) {
            this.selectedToolRequest = toolRequest;

            this.toolsNeededAtLine = [];
            this.toolsNeededToBeReturned = [];
            this.toolsExcludedFromReturn = [];
            this.toolsThatStayInERSA = [];

            const tableConfig = new TableConfig();
            tableConfig.pager.pageSize = -1;
            this.getDetailsForToolRequest(this.selectedToolRequest, tableConfig);
        } else {
            this.selectedToolRequest = null;
        }
    }

    onStepChange(stepIndex) {
        this.firstNextTried = false;
        switch (stepIndex) {
            case ToolReturnSteps.SELECT_LINE_AND_TOOL_REQUEST:
                this.currentStep = 1;
                break;
            case ToolReturnSteps.CONFIRM:
                this.currentStep = 2;
                break;
        }
    }

    onTryNext() {
        this.firstNextTried = true;
    }

    onConfirmClick() {
        this.reasonConfirmationModal.open();
    }

    onConfirmReasonReturn(reason: string) {
        this.returnReason = reason;
        this.reasonConfirmationModal.close();

        if (this.toolsThatStayInERSA && this.toolsThatStayInERSA.length > 0) {
            const toolIdsToBeReturned = this.getIdsArrayForSelectedTools(this.toolsNeededToBeReturned);
            const toolIdsInERSA = this.getIdsArrayForSelectedTools(this.toolsThatStayInERSA);

            const allReturnedToolsAreInERSA = (toolIdsToBeReturned.length === toolIdsInERSA.length) &&
                (toolIdsToBeReturned.every(val => toolIdsInERSA.includes(val)));
            this.ersaModal.open(this.toolsThatStayInERSA, allReturnedToolsAreInERSA);
        } else {
            this.confirmationModal.open();
        }
    }

    onConfirmReturn() {
        this.confirmationPopupMessage = '';
        this.isConfirmationInProgress = true;
        if (this.selectedToolRequest && this.selectedLine &&
            this.toolsNeededToBeReturned &&
            this.toolsNeededToBeReturned.length > 0) {

            let toolIds = this.getIdsArrayForSelectedTools(this.toolsNeededToBeReturned);
            let leftAtLineToolIds = [];
            const excludedToolIds = this.getIdsArrayForSelectedTools(this.toolsExcludedFromReturn);
            const reservedAtLineToolIds = this.getIdsArrayForSelectedTools(this.toolsReservedAtLine);

            if (this.toolsThatStayInERSareIncludedInReturn === false) {
                leftAtLineToolIds = this.getIdsArrayForSelectedTools(this.toolsThatStayInERSA);
                toolIds = toolIds.filter(item => !leftAtLineToolIds.includes(item))
            }

            const toolReturn = {
                lineId: this.selectedLine.id,
                toolRequestId: this.selectedToolRequest.id,
                toolIds: toolIds,
                excludedToolIds: excludedToolIds,
                leftAtLineToolIds: leftAtLineToolIds,
                reservedAtLineToolIds: reservedAtLineToolIds,
                reason: this.returnReason
            };

            this._logger.info('toolReturn:', toolReturn);
            this.toolReturnService.createToolReturn(toolReturn).subscribe(
                (data) => {
                    this.confirmationModal.close();
                    this._logger.info('getTollRequestsForLine:: API Returned data', data);
                    this.confirmationPopupMessage = this.buildCreateMessage(data.id);
                    this.isConfirmationInProgress = false;
                    this.logoutModal.open();
                },
                (error) => {
                    this.confirmationModal.close();
                    if (error && error.error) {
                        this.confirmationPopupMessage = this.translateService.instant(error.error.message);
                        this.errorModal.open();
                    }
                    this._logger.info('getToolRequestsForLine:: API Returned error', error);
                    this.isConfirmationInProgress = false;
                }
            );
        }
    }

    private getDetailsForToolRequest(toolRequest: ToolRequest, tableConfig: TableConfig) {
        this.toolsLoading = true;
        this._logger.info("getDetailsForToolRequest:: Retrieving Details for Tool Request", toolRequest);
        this.toolRequestService.getToolReturnRequestsByToolRequest(toolRequest, tableConfig, null).subscribe(
            (data) => {
                this._logger.info('getDetailsForToolRequest:: API Returned data', data);

                this.categorizeToolReturnTools(data.items);
                this.toolsLoading = false;
            },
            (error) => {
                this._logger.info('getDetailsForToolRequest:: API Returned error', error);
            }
        );
    }

    categorizeToolReturnTools(toolReturnTools) {
        toolReturnTools.forEach((tool: Tool | ToolSet) => {
            if (tool instanceof Tool) {
                if (tool.isNeededAtLine) {
                    this.toolsNeededAtLine.push(tool);
                } else if (tool.excludedFromReturn) {
                    this.toolsExcludedFromReturn.push(tool);
                } else if (tool.isAlreadyReservedForLine) {
                    this.toolsReservedAtLine.push(tool);
                } else {
                    this.toolsNeededToBeReturned.push(tool);
                }

                if (tool.isToolStationedAtERSA && !tool.isNeededAtLine) {
                    this.toolsThatStayInERSA.push(tool);
                }
            } else if (tool instanceof ToolSet) {
                const toolsNeededAtLineFiltered = tool.tools.filter(
                    (toolFromSet) => toolFromSet.isNeededAtLine
                );
                if (toolsNeededAtLineFiltered.length > 0) {
                    const setWithToolsNeededAtLine = Object.assign(new ToolSet(), tool);
                    setWithToolsNeededAtLine.tools = toolsNeededAtLineFiltered;

                    this.toolsNeededAtLine.push(setWithToolsNeededAtLine);
                }

                const toolsReservedAtLineFiltered = tool.tools.filter(
                    (toolFromSet) => toolFromSet.isAlreadyReservedForLine
                );
                if (toolsReservedAtLineFiltered.length > 0) {
                    const setWithToolsReservedAtLine = Object.assign(new ToolSet(), tool);
                    setWithToolsReservedAtLine.tools = toolsReservedAtLineFiltered;

                    this.toolsReservedAtLine.push(setWithToolsReservedAtLine);
                }

                const toolsNeededToBeReturnedFiltered = tool.tools.filter(
                    (toolFromSet) => !toolFromSet.isNeededAtLine &&
                                           !toolFromSet.excludedFromReturn &&
                                           !toolFromSet.isAlreadyReservedForLine
                );
                if (toolsNeededToBeReturnedFiltered.length > 0) {
                    const setWithToolsNeededToBeReturned = Object.assign(new ToolSet(), tool);
                    setWithToolsNeededToBeReturned.tools = toolsNeededToBeReturnedFiltered;

                    this.toolsNeededToBeReturned.push(setWithToolsNeededToBeReturned);
                }

                const toolsExcludedFromReturnFiltered = tool.tools.filter(
                    (toolFromSet) => !toolFromSet.isNeededAtLine && toolFromSet.excludedFromReturn
                );
                if (toolsExcludedFromReturnFiltered.length > 0) {
                    const setWithToolsExcludedFromReturn = Object.assign(new ToolSet(), tool);
                    setWithToolsExcludedFromReturn.tools = toolsExcludedFromReturnFiltered;

                    this.toolsExcludedFromReturn.push(setWithToolsExcludedFromReturn)
                }

                const toolsThatStayInERSAFiltered = tool.tools.filter(
                    (toolFromSet) => !toolFromSet.isNeededAtLine && toolFromSet.isToolStationedAtERSA
                );
                if (toolsThatStayInERSAFiltered.length > 0) {
                    const setWithToolsThatStayInERSA = Object.assign(new ToolSet(), tool);
                    setWithToolsThatStayInERSA.tools = toolsThatStayInERSAFiltered;

                    this.toolsThatStayInERSA.push(setWithToolsThatStayInERSA)
                }
            }
        });
    }

    getIdsArrayForSelectedTools(selectedTools: (Tool | ToolSet)[]) {
        const ids = [];
        for (const toolItem of selectedTools) {
            if (toolItem instanceof ToolSet) {
                for (const tool of toolItem.tools) {
                    ids.push(tool.id);
                }
            } else {
                ids.push(toolItem.id);
            }
        }
        return ids;
    }

    canPerformReturn() {
        return (
            this.toolsNeededAtLine &&
            this.toolsReservedAtLine &&
            this.toolsNeededToBeReturned &&
            (this.toolsNeededAtLine.length > 0 ||
            this.toolsReservedAtLine.length > 0) &&
            this.toolsNeededToBeReturned.length === 0
        )
    }

    buildCreateMessage(toolReturnRequestName) {
        return this.translateService.instant('lineSetter.tool-returns.toasts.created', {
            toolReturnRequestName: toolReturnRequestName
        });
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('VIEW_TOOL_RETURNS');
    }

    onSelectionErsaBay(value) {
        this.toolsThatStayInERSareIncludedInReturn = value;

        this.ersaModal.close();
        this.confirmationModal.open();
    }

    get hasNonReturnableTools(): boolean {
        return this.selectedToolRequest?.isForNonReturnableTools === true;
    }
}
